'use client';

import { useRef, useCallback } from 'react';
import { ArrowLeft, Save, X } from 'lucide-react';
import { Artifact } from '../mockData';
import { ViewerAnnotation } from './types';
import { MarkdownRenderer } from './MarkdownRenderer';
import { AnnotationToolbar } from './AnnotationToolbar';
import { AnnotationPanel } from './AnnotationPanel';
import { useTextSelection } from './useTextSelection';

interface ArtifactEditorProps {
  artifact: Artifact;
  content: string;
  annotations: ViewerAnnotation[];
  hasUnsavedChanges: boolean;
  onAddAnnotation: (annotation: Omit<ViewerAnnotation, 'id' | 'createdAt'>) => void;
  onDeleteAnnotation: (id: string) => void;
  onSave: () => void;
  onClose: () => void;
}

export function ArtifactEditor({
  artifact,
  content,
  annotations,
  hasUnsavedChanges,
  onAddAnnotation,
  onDeleteAnnotation,
  onSave,
  onClose,
}: ArtifactEditorProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { selection, clearSelection } = useTextSelection(contentRef);

  const handleAddAnnotation = useCallback(
    (type: ViewerAnnotation['type'], comment?: string) => {
      if (!selection) return;

      onAddAnnotation({
        type,
        text: selection.text,
        comment,
        position: {
          blockIndex: selection.blockIndex,
          startOffset: selection.startOffset,
          endOffset: selection.endOffset,
        },
      });

      clearSelection();
    },
    [selection, onAddAnnotation, clearSelection]
  );

  const handleAnnotationClick = useCallback((annotation: ViewerAnnotation) => {
    // Find and scroll to the highlighted element
    const element = document.querySelector(`[data-annotation-id="${annotation.id}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash effect
      element.classList.add('ring-2', 'ring-primary');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary');
      }, 1500);
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-text rounded-lg hover:bg-bg-alt transition-colors"
            title="Back to artifacts"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="font-medium text-text">{artifact.name}</h2>
            <p className="text-xs text-text-muted">{artifact.path}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-xs text-yellow-500 mr-2">Unsaved changes</span>
          )}
          <button
            onClick={onSave}
            disabled={!hasUnsavedChanges}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              hasUnsavedChanges
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-bg-alt text-text-muted cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            Save
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-text rounded-lg hover:bg-bg-alt transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div
            ref={contentRef}
            className="max-w-3xl mx-auto bg-bg-card border border-border rounded-xl p-6 shadow-sm"
          >
            <MarkdownRenderer content={content} annotations={annotations} />
          </div>
        </div>

        {/* Annotation Panel */}
        <div className="w-72 shrink-0 hidden lg:block">
          <AnnotationPanel
            annotations={annotations}
            onDeleteAnnotation={onDeleteAnnotation}
            onAnnotationClick={handleAnnotationClick}
          />
        </div>
      </div>

      {/* Floating toolbar */}
      <AnnotationToolbar
        selection={selection}
        onAddAnnotation={handleAddAnnotation}
        onClose={clearSelection}
      />
    </div>
  );
}
